import { useEffect, useState } from "react";
import { getSection, peekSection, type SectionItem, ApiError } from "@/api";
import { useI18n, useT } from "@/i18n";
import Skeleton from "@/components/Skeleton";
import NewsCard from "@/components/NewsCard";
import FundraiserCard from "@/components/FundraiserCard";
import ReportCard from "@/components/ReportCard";
import { getProgress } from "@/lib/getProgress";

type Filter = "all" | "news" | "reports";

export default function NewsScreen() {
  const { lang, t } = useI18n();
  const tf = useT();

  const [newsItems, setNewsItems] = useState<SectionItem[]>(() => peekSection("news", lang) ?? []);
  const [reportItems, setReportItems] = useState<SectionItem[]>(() => peekSection("reports", lang) ?? []);
  const [fundraisings, setFundraisings] = useState<SectionItem[]>(() => peekSection("fundraisings", lang) ?? []);
  const [loading, setLoading] = useState(() => peekSection("news", lang) === undefined);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");

  useEffect(() => {
    let cancelled = false;
    if (peekSection("news", lang) === undefined) setLoading(true);
    setError(null);

    Promise.all([
      getSection("news", lang),
      getSection("reports", lang),
      getSection("fundraisings", lang),
    ])
      .then(([news, reports, funds]) => {
        if (cancelled) return;
        setNewsItems(news);
        setReportItems(reports);
        setFundraisings(funds);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Error loading");
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [lang]);

  const displayed =
    filter === "all"
      ? [...newsItems, ...reportItems].sort((a, b) =>
          (b.date ?? "").localeCompare(a.date ?? ""),
        )
      : filter === "news"
      ? newsItems
      : reportItems;

  const chips: { key: Filter; label: string }[] = [
    { key: "all",     label: tf("news_all") },
    { key: "news",    label: t("news")      },
    { key: "reports", label: t("reports")   },
  ];

  return (
    <div style={{ background: "var(--tg-bg)", minHeight: "100%" }}>
      {/* Chip row — sticky so filter stays accessible while scrolling */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          display: "flex",
          gap: 8,
          padding: "12px 16px",
          background: "var(--tg-bg)",
          borderBottom: "1px solid var(--tg-secondary-bg)",
        }}
      >
        {chips.map(({ key, label }) => {
          const active = filter === key;
          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              style={{
                padding: "6px 14px",
                borderRadius: 999,
                border: active ? "none" : "1.5px solid var(--tg-secondary-bg)",
                background: active ? "var(--brand-blue)" : "transparent",
                color: active ? "#ffffff" : "var(--muted)",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {loading && <Skeleton count={4} />}

      {!loading && error && (
        <p style={{ color: "var(--danger)", textAlign: "center", padding: "48px 32px" }}>
          {error}
        </p>
      )}

      {!loading && !error && displayed.length === 0 && (
        <p style={{ color: "var(--tg-hint)", textAlign: "center", padding: "48px 32px" }}>
          {t("empty_state")}
        </p>
      )}

      {!loading && !error && displayed.length > 0 && filter === "reports" && (
        <div style={{ padding: "16px 16px 24px" }}>
          {displayed.map(i => <ReportCard key={i.id} item={i} />)}
        </div>
      )}

      {!loading && !error && displayed.length > 0 && filter !== "reports" && (
        <NewsFeaturedGrid items={displayed} fundraisings={fundraisings} filter={filter} />
      )}
    </div>
  );
}

function NewsFeaturedGrid({
  items,
  fundraisings,
  filter,
}: {
  items: SectionItem[];
  fundraisings: SectionItem[];
  filter: Filter;
}) {
  const { t } = useI18n();
  const featured = items[0];
  const rest = items.slice(1);
  if (!featured) return null;

  const showFundraiser = filter !== "reports" && fundraisings.length > 0;
  const biggest = showFundraiser
    ? [...fundraisings].sort(
        (a, b) =>
          (getProgress(b.id).target ?? 0) - (getProgress(a.id).target ?? 0),
      )[0]
    : null;

  return (
    <div style={{ padding: "16px 16px 24px" }}>
      <NewsCard item={featured} variant="featured" />

      {biggest && (
        <>
          <p
            style={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--tg-hint)",
              margin: "16px 0 12px",
            }}
          >
            {t("active_fundraisers")}
          </p>
          <div style={{ marginBottom: 16 }}>
            <FundraiserCard
              id={biggest.id}
              kind={biggest.kind}
              title={biggest.title}
              subtitle={biggest.subtitle}
              image={biggest.image}
            />
          </div>
        </>
      )}

      {rest.length > 0 && (
        <div
          style={{
            borderRadius: "var(--r-card)",
            background: "var(--tg-bg, #ffffff)",
            border: "1px solid var(--border, rgba(0,0,0,0.08))",
            boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
            padding: "0 14px",
          }}
        >
          {rest.map((item, i) =>
            item.kind === "reports" ? (
              <ReportCard key={item.id} item={item} />
            ) : (
              <NewsCard key={item.id} item={item} variant="compact" divider={i > 0} />
            ),
          )}
        </div>
      )}
    </div>
  );
}
